import React, { useEffect, useId, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, ChevronDown, ChevronRight, Send, Clock, ShieldCheck, MapPin, CheckCircle2 } from 'lucide-react';
import { PHONE_DISPLAY, PHONE_TEL, STEPS, IMAGES } from '@/lib/site';
import { submitLead, trackCallClick, trackEvent } from '@/lib/analytics';
import { useToast } from '@/hooks/use-toast';

export function CallLink({ label, source = 'cta' }) {
  return (
    <a
      href={PHONE_TEL}
      onClick={() => trackCallClick(source)}
      className="group inline-flex items-center justify-center gap-2 rounded-full bg-water px-6 py-3 text-sm font-bold text-navy-deep shadow-lg shadow-water/30 transition hover:bg-water-strong hover:shadow-water/50 hover:-translate-y-0.5"
    >
      <Phone className="h-4 w-4" strokeWidth={2.4} />
      {label || `Call ${PHONE_DISPLAY}`}
    </a>
  );
}

export function SectionHeading({ eyebrow, title, sub, center = false }) {
  return (
    <div className={center ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'}>
      {eyebrow && (
        <span className="inline-block rounded-full bg-water-light px-3 py-1 text-xs font-semibold uppercase tracking-wider text-water-strong">
          {eyebrow}
        </span>
      )}
      <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight text-primary sm:text-4xl">{title}</h2>
      {sub && <p className="mt-4 text-base leading-relaxed text-muted-foreground">{sub}</p>}
    </div>
  );
}

export function HowItWorks() {
  return (
    <section className="bg-secondary py-20">
      <div className="container-page">
        <SectionHeading center eyebrow="How It Works" title="From Emergency Call to Full Recovery" sub="A clear four-step process for getting water damage help." />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => { const StepIcon=[Phone,MapPin,ShieldCheck,CheckCircle2][i]||CheckCircle2; return (
            <div key={s.n} className="relative rounded-2xl bg-white p-6 ring-1 ring-border transition hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/5">
              <div className="flex items-center justify-between"><span className="flex h-11 w-11 items-center justify-center rounded-xl bg-water-light text-water-strong"><StepIcon className="h-5 w-5"/></span><span className="font-display text-3xl font-black text-water/25">{s.n}</span></div>
              <h3 className="mt-4 font-display text-lg font-bold text-primary">{s.title}</h3><p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>{i<STEPS.length-1&&<span className="absolute -right-4 top-1/2 hidden -translate-y-1/2 rounded-full bg-secondary p-1 text-water lg:block"><ChevronRight className="h-5 w-5"/></span>}
            </div>); })}
        </div>
      </div>
    </section>
  );
}

export function EmergencyCta() {
  return (
    <section className="relative overflow-hidden bg-navy-deep py-16">
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${IMAGES.basement})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      <div className="absolute inset-0 bg-gradient-to-r from-navy-deep via-navy-deep/95 to-navy-deep/70" />
      <div className="container-page relative flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-water ring-1 ring-white/15">
            <span className="h-2 w-2 rounded-full bg-water animate-pulse" />
            24/7 Water Damage Requests
          </span>
          <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight text-white sm:text-4xl">
            Water Damage Emergency? <span className="text-water">Get local help now.</span>
          </h2>
          <p className="mt-3 max-w-lg text-white/70">Get connected with local water damage help in Coquitlam, Port Coquitlam or Port Moody.</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <CallLink label={`CALL ${PHONE_DISPLAY}`} source="emergency-cta" />
          <Link to="/contact" className="inline-flex items-center justify-center gap-1 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10">
            Send Request <Send className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function FaqSection({ faqs, eyebrow = 'FAQ', title = 'Common Questions About Water Damage', sub = 'Clear answers to common questions about water damage, cleanup, drying and next steps.', showHelpBox = true }) {
  const location = useLocation();
  const faqId = useId().replace(/:/g, '');
  const [open, setOpen] = useState(-1);

  useEffect(() => {
    setOpen(-1);
  }, [location.pathname, location.key]);
  return (
    <section className="py-20">
      <div className="container-page grid gap-12 lg:grid-cols-[1fr_1.4fr]">
        <div>
          <SectionHeading eyebrow={eyebrow} title={title} sub={sub} />
          {showHelpBox && (
            <div className="mt-8 hidden lg:flex items-center gap-3 rounded-2xl border border-border bg-secondary p-5">
              <ShieldCheck className="h-8 w-8 text-water shrink-0" strokeWidth={1.8} />
              <p className="text-sm text-muted-foreground">Still not sure what service fits? Describe what happened on the contact page and start from the visible problem.</p>
            </div>
          )}
        </div>
        <div className="space-y-3">
          {faqs.map((f, i) => {
            const questionId = `${faqId}-question-${i}`;
            const answerId = `${faqId}-answer-${i}`;
            const isOpen = open === i;
            return (
              <div key={f.q} className={`rounded-2xl border transition ${isOpen ? 'border-water/40 bg-white shadow-md shadow-navy/5' : 'border-border bg-white hover:border-water/30'}`}>
                <button
                  id={questionId}
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={answerId}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="flex w-full items-center justify-between gap-4 p-5 text-left"
                >
                  <span className="font-display text-base font-semibold text-primary">{f.q}</span>
                  <ChevronDown aria-hidden="true" className={`h-5 w-5 shrink-0 text-water transition ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div id={answerId} role="region" aria-labelledby={questionId} className="px-5 pb-5 text-sm leading-relaxed text-muted-foreground">{f.a}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function LeadForm({ source = 'generic', compact = false }) {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formStarted, setFormStarted] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', city: 'Coquitlam', damageType: 'Basement flooding', message: '' });

  const onFormFocus = () => { if (!formStarted) { setFormStarted(true); trackEvent('form_start', { source, page_path: window.location.pathname }); } };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: 'Please fill required fields', description: 'Name and phone are needed so a provider can reach you.' });
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({ ...form, source, page: window.location.pathname });
      setSubmitted(true);
      toast({ title: 'Request received', description: 'Your request was sent successfully.' });
    } catch (err) {
      toast({ title: 'Could not send request', description: 'Please call us directly for immediate help.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-2xl bg-white p-8 shadow-2xl shadow-navy/20 ring-1 ring-water/20">
        <div className="flex flex-col items-center text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-water-light">
            <CheckCircle2 className="h-8 w-8 text-water-strong" />
          </span>
          <h3 className="mt-4 font-display text-xl font-bold text-primary">Request Received</h3>
          <p className="mt-2 text-sm text-muted-foreground">Your request has been recorded. If water is actively spreading or the situation cannot wait, use the call option below.</p>
          <CallLink label={`CALL ${PHONE_DISPLAY}`} source={`${source}-success`} />
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} onFocus={onFormFocus} noValidate className="rounded-2xl bg-white p-6 shadow-2xl shadow-navy/20 ring-1 ring-white/60 sm:p-7">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-water-light text-water-strong">
          <Send className="h-4 w-4" />
        </span>
        <div>
          <h3 className="font-display text-lg font-bold text-primary">Get Connected Fast</h3>
          <p className="text-xs text-muted-foreground">24/7 request line for Tri-Cities homeowners</p>
        </div>
      </div>
      <div className="mt-5 grid gap-3">
        <label className="block">
          <span className="text-xs font-semibold text-primary">Full name *</span>
          <input name="name" autoComplete="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2.5 text-sm outline-none transition focus:border-water focus:ring-2 focus:ring-water/20" placeholder="e.g. Sarah Nguyen" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-primary">Phone *</span>
          <input name="phone" type="tel" inputMode="tel" autoComplete="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2.5 text-sm outline-none transition focus:border-water focus:ring-2 focus:ring-water/20" placeholder="(604) 555-0123" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-primary">Email <span className="font-normal text-muted-foreground">(optional)</span></span>
          <input name="email" type="email" inputMode="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2.5 text-sm outline-none transition focus:border-water focus:ring-2 focus:ring-water/20" placeholder="e.g. sarah@email.com" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-primary">City</span>
          <select name="city" autoComplete="address-level2" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2.5 text-sm outline-none transition focus:border-water focus:ring-2 focus:ring-water/20">
            <option>Coquitlam</option>
            <option>Port Coquitlam</option>
            <option>Port Moody</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-primary">Type of damage</span>
          <select name="damageType" value={form.damageType} onChange={(e) => setForm({ ...form, damageType: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2.5 text-sm outline-none transition focus:border-water focus:ring-2 focus:ring-water/20">
            <option>Basement flooding</option>
            <option>Burst pipe</option>
            <option>Water leak</option>
            <option>Storm water</option>
            <option>Sewage backup</option>
            <option>Mould or moisture concern</option>
            <option>Other</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-primary">Describe your problem <span className="font-normal text-muted-foreground">(optional)</span></span>
          <textarea name="message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={compact ? 2 : 4} className="mt-1 w-full resize-y rounded-lg border border-border bg-white px-3 py-2.5 text-sm outline-none transition focus:border-water focus:ring-2 focus:ring-water/20" placeholder="e.g. Burst pipe in the basement and water is spreading across the floor" />
        </label>
        <button type="submit" disabled={submitting} className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-navy-deep px-6 py-3 text-sm font-bold text-white transition hover:bg-navy hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60">
          {submitting ? 'Sending…' : 'Get Help Now'} <ChevronRight className="h-4 w-4" />
        </button>
        <p className="text-center text-xs font-semibold text-primary/75">No obligation. No spam. Your request is used to connect you with local water-damage help.</p>
        <p className="text-center text-[10px] leading-relaxed text-muted-foreground">By submitting, you agree that your request may be shared with a local restoration provider so they can contact you about this water-damage request. <Link to="/privacy" className="underline underline-offset-2 hover:text-primary">Privacy</Link></p>
        <div className="mt-1 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> 24/7</span>
          <span className="flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Local provider network</span>
          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> Local Tri-Cities</span>
        </div>
      </div>
    </form>
  );
}
